import React from 'react';
import { useTranslation } from 'react-i18next';
import { IProjectPhase, PhaseType, PHASE_TYPE_LABELS, PHASE_STATUS_COLORS } from '@/types/project';

interface PhaseTimelineProps {
  phases: IProjectPhase[];
  currentPhaseId?: string;
  onPhaseClick?: (phase: IProjectPhase) => void;
}

const PhaseTimeline: React.FC<PhaseTimelineProps> = ({ phases, currentPhaseId, onPhaseClick }) => {
  const { t } = useTranslation('project');

  // Sort phases by sort_order
  const sortedPhases = [...phases].sort((a, b) => a.sort_order - b.sort_order);

  // Find current phase index
  const currentIndex = sortedPhases.findIndex(p => p.id === currentPhaseId);

  const getPhaseStatus = (index: number): 'completed' | 'current' | 'upcoming' => {
    if (currentIndex === -1) {
      // If no current phase, check phase status
      const phase = sortedPhases[index];
      if (phase.status === 'completed') return 'completed';
      if (phase.status === 'in_progress' || phase.status === 'review') return 'current';
      return 'upcoming';
    }
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  };

  const getNodeClass = (status: 'completed' | 'current' | 'upcoming') => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-500 border-emerald-500 text-white';
      case 'current':
        return 'bg-blue-500 border-blue-500 text-white ring-4 ring-blue-200 dark:ring-blue-800';
      case 'upcoming':
        return 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400';
    }
  };

  const getLineClass = (status: 'completed' | 'current' | 'upcoming') => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-500';
      case 'current':
        return 'bg-gradient-to-r from-emerald-500 to-gray-300 dark:to-gray-600';
      case 'upcoming':
        return 'bg-gray-300 dark:bg-gray-600';
    }
  };

  if (sortedPhases.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        {t('phases.noPhases')}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
        {t('phases.timeline')}
      </h3>

      {/* Desktop view - horizontal timeline */}
      <div className="hidden md:block overflow-x-auto">
        <div className="flex items-center justify-between min-w-max px-4">
          {sortedPhases.map((phase, index) => {
            const status = getPhaseStatus(index);
            const isLast = index === sortedPhases.length - 1;

            return (
              <React.Fragment key={phase.id}>
                {/* Phase node */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => onPhaseClick?.(phase)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${getNodeClass(status)} ${onPhaseClick ? 'cursor-pointer hover:scale-110' : ''}`}
                    title={phase.phase_name}
                  >
                    {status === 'completed' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-xs font-bold">{index + 1}</span>
                    )}
                  </button>
                  <span className={`mt-2 text-xs font-medium whitespace-nowrap ${
                    status === 'current' ? 'text-blue-600 dark:text-blue-400' :
                    status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {PHASE_TYPE_LABELS[phase.phase_type as PhaseType] || phase.phase_name}
                  </span>
                  {status === 'current' && (
                    <span className="text-[10px] text-blue-500 dark:text-blue-400 font-medium">
                      {t('phases.current')}
                    </span>
                  )}
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div className={`flex-1 h-0.5 mx-2 min-w-[40px] ${getLineClass(status)}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile view - vertical timeline */}
      <div className="md:hidden">
        <div className="flex flex-col space-y-2">
          {sortedPhases.map((phase, index) => {
            const status = getPhaseStatus(index);

            return (
              <div key={phase.id} className="flex items-center gap-3">
                {/* Phase node */}
                <button
                  onClick={() => onPhaseClick?.(phase)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${getNodeClass(status)} ${onPhaseClick ? 'cursor-pointer' : ''}`}
                >
                  {status === 'completed' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-xs font-bold">{index + 1}</span>
                  )}
                </button>

                {/* Phase info */}
                <div className="flex-1 flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    status === 'current' ? 'text-blue-600 dark:text-blue-400' :
                    status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {PHASE_TYPE_LABELS[phase.phase_type as PhaseType] || phase.phase_name}
                  </span>
                  {status === 'current' && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${PHASE_STATUS_COLORS[phase.status]}`}>
                      {t('phases.current')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PhaseTimeline;
